import { Button, Form, Stack } from 'react-bootstrap';
import { ChangeEvent, useState } from 'react';

import { IBasePagedModel } from '../../interfaces/PagedSortedSearch';
import classNames from 'classnames';

const AVAILABLE_PAGE_SIZES = [10, 50, 100];

export function CheckmatePagination(props: {
    hidePageSizeSelector?: boolean;
    pagedModel?: IBasePagedModel;
    pagedModelPrefixText?: string;
    pagedModelSuffixText?: string;
    onPageChange: (page: number) => void;
    onPageSizeChange?: (pageSize: number) => void;
    sticky?: boolean;
}) {
    if (!props.pagedModel || (props.pagedModel.totalResults ?? 0) === 0) {
        return null;
    }

    const [pageSize, setPageSize] = useState<number>(
        props.pagedModel.pageSize ?? AVAILABLE_PAGE_SIZES.at(-1)
    );

    const handleFirstPageClick = () => {
        if (props.pagedModel?.pageNumber === 1) {
            return;
        }
        props.onPageChange(1);
    };

    const handleLastPageClick = () => {
        if (props.pagedModel?.pageNumber === props.pagedModel?.totalPages) {
            return;
        }

        props.onPageChange(props.pagedModel?.totalPages ?? 0);
    };

    const handlePrevPageClick = () => {
        if (props.pagedModel?.pageNumber === 1) {
            return;
        }

        props.onPageChange((props.pagedModel?.pageNumber ?? 0) - 1);
    };

    const handleNextPageClick = () => {
        if (props.pagedModel?.pageNumber === props.pagedModel?.totalPages) {
            return;
        }

        props.onPageChange((props.pagedModel?.pageNumber ?? 0) + 1);
    };

    const handlePageSizeChange = (event: ChangeEvent<HTMLSelectElement>) => {
        setPageSize(parseInt(event.target.value));
        props.onPageSizeChange?.(parseInt(event.target.value));
    };

    if (props.pagedModel.totalResults === 0) {
        return null;
    }

    return (
        <Stack
            direction="horizontal"
            style={{
                backgroundColor: 'white',
                flexWrap: 'wrap',
                fontSize: '0.9em',
                justifyContent: 'space-between',
                paddingBottom: props.sticky ? '1em' : '2em',
                paddingRight: '2em',
                paddingTop: props.sticky ? '1em' : 0,
                rowGap: '1em',
            }}
            className={classNames(['color-darkgray',{ 'sticky-bottom': props.sticky }])}
            gap={2}
        >
            <Stack direction="horizontal" gap={2}>
                <span className="nowrap">
                    Page {props.pagedModel.pageNumber} of {props.pagedModel.totalPages}
                </span>
                {props.pagedModel.totalPages !== 1 && (
                    <Stack direction="horizontal" gap={1}>
                        <Button
                            disabled={props.pagedModel.pageNumber === 1}
                            onClick={handleFirstPageClick}
                            size="sm"
                            variant="default"
                        >
                            <i className="fal fa-angle-double-left" />
                        </Button>
                        <Button
                            disabled={props.pagedModel.pageNumber === 1}
                            onClick={handlePrevPageClick}
                            size="sm"
                            variant="default"
                        >
                            Prev
                        </Button>
                        <Button
                            disabled={props.pagedModel.pageNumber === props.pagedModel.totalPages}
                            onClick={handleNextPageClick}
                            size="sm"
                            variant="default"
                        >
                            Next
                        </Button>
                        <Button
                            disabled={props.pagedModel.pageNumber === props.pagedModel.totalPages}
                            onClick={handleLastPageClick}
                            size="sm"
                            variant="default"
                        >
                            <i className="fal fa-angle-double-right" />
                        </Button>
                    </Stack>
                )}
            </Stack>
            <Stack direction="horizontal" gap={3}>
                {!props.hidePageSizeSelector && (
                    <>
                        <Stack direction="horizontal" gap={1} className="align-items-center">
                            <span style={{ width: 68 }}>
                                <Form.Select
                                    className="input-control color-darkgray"
                                    id="pageSize"
                                    name="pageSize"
                                    onChange={handlePageSizeChange}
                                    size="sm"
                                    value={pageSize}
                                >
                                    {AVAILABLE_PAGE_SIZES.map((opt) => (
                                        <option key={opt} value={opt}>
                                            {opt}
                                        </option>
                                    ))}
                                </Form.Select>
                            </span>
                            <span>rows per page</span>
                        </Stack>
                        <div className="text-gray">|</div>
                    </>
                )}
                <Stack direction="horizontal" gap={1} className="align-items-center">
                    <span>{props?.pagedModelPrefixText ?? 'Showing rows'}</span>
                    <span className="bold">
                        {props.pagedModel.recordStartIndex}-{props.pagedModel.recordEndIndex}
                    </span>
                    <span>of</span>
                    <span className="bold">{props.pagedModel.totalResults ?? 0}</span>
                    {props.pagedModelSuffixText && <span>{props?.pagedModelSuffixText ?? ''}</span>}
                </Stack>
            </Stack>
        </Stack>
    );
}
